import { AB_WORD_REAL_HISTORY } from "@/constants/ABMng";
import useABTest from "@/utils/hooks/useABtest";
import React from "react";
import { useInspirationWords } from "../useInspirationWords";
import { useDetailPageEventListener } from "./useDetailPageEventListener";
import { useInspirationCardRenderer } from "./useInspirationCardRenderer";
import { useMasonryRelayout } from "./useMasonryRelayout";
import { usePageRouteState } from "./usePageRouteState";

export interface UseDiscoveryInspirationWordsOptions {
  /** 分类标识（用于数据隔离） */
  categoryKey: string;
}

export interface UseDiscoveryInspirationWordsReturn {
  /** 自定义渲染函数（直接传给 PinList），AB未开启时为null */
  renderPinCard:
    | ((
        record: any,
        index: number,
        defaultRender: () => React.ReactElement
      ) => React.ReactNode)
    | null;
}

/**
 * 检查 AB 实验是否开启
 *
 * ⚠️ 重要：请在组件层使用此 Hook 做条件判断，而不是在 Hook 内部判断
 *
 * @returns true 表示 AB 开启，可以使用灵感词功能
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isEnabled = useIsInspirationWordsEnabled();
 *
 *   // 条件调用 Hook（符合 React 规范）
 *   const result = isEnabled
 *     ? useDiscoveryInspirationWords({ categoryKey })
 *     : { renderPinCard: null };
 *
 *   return <PinList renderPinCard={result.renderPinCard} />;
 * }
 * ```
 */
export function useIsInspirationWordsEnabled(): boolean {
  const abTest = useABTest(AB_WORD_REAL_HISTORY);
  return abTest.inspirationWords;
}

/**
 * 主 Hook: 发现页灵感词管理
 *
 * 职责：
 * - AB实验层：控制功能开关
 * - 数据层：useInspirationWords (管理灵感词数据)
 * - 状态层：usePageRouteState (监听页面路由状态)
 * - 效果层：useMasonryRelayout (控制瀑布流重绘)
 * - 事件层：useDetailPageEventListener (监听详情页事件)
 * - 渲染层：useInspirationCardRenderer (提供自定义渲染逻辑)
 *
 * ⚠️ React Hooks 规范：所有 Hooks 始终会被调用（无论 AB 是否开启），
 * 只根据 AB 结果决定返回值。这符合 React Hooks 规则，避免条件调用。
 *
 * @example
 * ```tsx
 * const { renderPinCard } = useDiscoveryInspirationWords({
 *   categoryKey: categoryId || "all",
 * });
 *
 * <PinList
 *   dataSource={list}
 *   renderPinCard={renderPinCard}
 * />
 * ```
 */
export function useDiscoveryInspirationWords(
  options: UseDiscoveryInspirationWordsOptions
): UseDiscoveryInspirationWordsReturn {
  const { categoryKey } = options;

  // 0. AB 实验控制
  const isEnabled = useIsInspirationWordsEnabled();

  // 1. 数据层：管理灵感词数据（传递 enabled 参数）
  const { recordOperation, displayedModules } = useInspirationWords({
    pageScope: "pin_recommend_word",
    categoryKey,
    enabled: isEnabled,
  });

  // 2. 状态层：监听页面路由状态（传递 enabled 参数）
  const { isDetailPageOpen } = usePageRouteState(isEnabled);

  // 3. 效果层：控制瀑布流重绘（传递 enabled 参数）
  useMasonryRelayout(displayedModules.size, isDetailPageOpen, isEnabled);

  // 4. 事件层：监听详情页事件（传递 enabled 参数）
  useDetailPageEventListener(recordOperation, isEnabled);

  // 5. 渲染层：提供自定义渲染函数
  const renderPinCard = useInspirationCardRenderer(
    displayedModules,
    recordOperation,
    isDetailPageOpen
  );

  // 6. 根据 AB 结果返回
  return {
    renderPinCard: isEnabled ? renderPinCard : null,
  };
}
